import { MediaRecorder, load } from "@zappar/mediarecorder";
import { DoubleSide, Mesh, MeshBasicMaterial, PlaneGeometry } from 'three';
import { hideDownloadPage, hideDraw, hideDrawTools, revealDownloadData, revealDownloadPage, revealDraw, setDownloadForiOS, startCameraButtonAnim, stopCameraButtonAnim } from '../../../dom';
import Renderer from '../../Renderer';
import { awaitTimer, getMobileOS, isIOS } from '../../utils';
import { LoadedTextures } from '../../utils/types';
import { triggerDrawControl } from "../../..";
import { sendTrackingEvent } from "../../../utils/events";

export class Snapshot {
    private renderer: Renderer;
    public declare watermark: Mesh;
    private canvas: HTMLCanvasElement;
    private recorder!: MediaRecorder;
    private recording!: boolean;
    private isVid: boolean;
    private vidTimerComplete: boolean
    private vidTimeout!: ReturnType<typeof setTimeout>;
    private canvasStream!: MediaStream;
    private videoPlaying: boolean

    private downloadImage: HTMLImageElement;
    private downloadVideo: HTMLVideoElement;

    private data: Blob[];
    private vidData: any;
    private imgData: any;
    private recorderError: boolean

    constructor(renderer: Renderer, textures: LoadedTextures) {
        this.renderer = renderer;
        this.downloadImage = <HTMLImageElement>document.querySelector(".download_image");
        this.downloadVideo = <HTMLVideoElement>document.querySelector(".download_video");
        this.isVid = false
        this.vidTimerComplete = false;
        this.videoPlaying = false;
        this.canvas = document.querySelector("canvas")!;
        this.canvas.width += this.canvas.width % 2;
        this.canvas.height += this.canvas.height % 2;
        this.canvasStream = this.canvas.captureStream(30);

        this.data = [];
        this.recorderError = false;

        this.setUpWatermark(textures);

        this.downloadVideo.addEventListener("playing", () => {
            if (this.recording) {
                this.recording = false;
                //hideLoadingPage();

                hideDraw();
                awaitTimer(500).then(() => {
                    revealDownloadPage();
                    revealDownloadData(this.isVid);
                    triggerDrawControl('pause')
                })

            }
        });

        this.resetVideo();


        if (getMobileOS() == "iOS") {
            setDownloadForiOS()
        }

    }

    public setUpWatermark = (textures: LoadedTextures) => {
        this.watermark = new Mesh(
            new PlaneGeometry(1, 1),
            new MeshBasicMaterial({
                map: textures.watermark,
                transparent: true,
                side: DoubleSide,
            })
        );
        this.watermark.visible = false;
        this.watermark.scale.set(0.5, 0.21, 1);
        let posx = (window.innerWidth / window.innerHeight) - (this.watermark.scale.x / 2);
        console.log('logo pos', window.innerHeight, window.innerWidth, posx)
        this.watermark.position.set(-posx, 1 - (this.watermark.scale.y / 2), -1.4);

    }

    public toggleWatermark = () => {
        console.log('toggle watermark')
        if (this.watermark != null)
            this.watermark.visible = !this.watermark.visible;
    }

    private resetVideo() {
        console.log('reset video')
        this.videoPlaying = false;
        this.downloadVideo.pause();
        this.downloadVideo.removeAttribute("src"); // empty source
        this.downloadVideo.load();
        this.vidData = "";
        this.data = [];

        try {
            this.recorder = new MediaRecorder(this.canvasStream, { mimeType: "video/mp4" });
        } catch (err) {
            console.log("Error creating Media Recorder, can take photo only");
        }



        if (this.recorder != null && !this.recorderError) {

            this.recorder.onerror = (err: any) => {
                console.log('error recording stream: ' + err.message);
                this.recorderError = true;
            };

            if (!this.recorderError) {
                this.recorder.ondataavailable = evt => {
                    this.data.push(evt.data);
                };

                this.recorder.onstop = async (evt: any) => {
                    console.log("Recorder.onStop");
                    sendTrackingEvent('User took video');
                    stopCameraButtonAnim();
                    this.isVid = this.vidTimerComplete;
                    this.vidData = await this.blobToDataURL(new Blob(this.data, { type: "video/mp4" }));
                    this.downloadVideo.src = this.vidData;
                    this.videoPlaying = true;
                    this.downloadVideo.play();
                };
            }
        }
    }


    private blobToDataURL(blob: Blob): Promise<string> {
        console.log('blob to data')
        return new Promise(resolve => {
            const a = new FileReader();
            a.onload = evt => resolve(a.result as string);
            a.readAsDataURL(blob);
        });
    }

    private async navigatorShare(data: any) {
        console.log('navigator share')
        const image = await fetch(data);
        const blob = await image.blob();
        let file: any;

        if (this.isVid) {
            file = new File([blob], "sharpie.mp4", { type: "video/mp4" });
        } else {
            file = new File([blob], "sharpie.jpg", { type: "image/jpeg" });
        }

        if (
            blob &&
            navigator.canShare &&
            navigator.canShare({
                text: '',
                title: '',
                files: [file],
            })
        ) {
            await navigator
                .share({
                    text: '',
                    title: '',
                    files: [file],
                })
                .then(() => {
                    console.log("Share was successful.");
                    sendTrackingEvent('User shared photo/video');

                })
                .catch(error => {
                    console.warn("Sharing failed", error);
                });
        } else {
            console.log(`Your system doesn't support sharing files.`);
            if (this.isVid) {
                this.saveToDownloads(blob);
            } else {
                this.saveToDownloads(blob);
            }
        }
    }

    private saveToDownloads(blob: any) {
        console.log('save to downloads')
        const imageURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = imageURL;
        link.download = "#sharpie";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private stopRecording() {
        console.log('stop recording')
        if (this.recording) {
            this.recorder.stop();
        }
    }

    public cameraDown() {
        console.log('camera down')
        hideDrawTools();
        this.vidTimerComplete = false;
        this.vidTimeout = setTimeout(() => {
            if (this.recorder != null && !this.recorderError) {
                this.toggleWatermark()
                console.log("Video recording started");
                startCameraButtonAnim();
                this.vidTimerComplete = true;

                this.canvas.width += this.canvas.width % 2;
                this.canvas.height += this.canvas.height % 2;

                this.recorder.start();
                this.recording = true;
            }
        }, 300);

        setTimeout(() => {
            console.log("recorder stop after timeout");
            if (this.recording) this.recorder.stop();
        }, 15000);
    };

    public cameraUp() {
        console.log('camera up')
        this.isVid = this.vidTimerComplete;
        clearTimeout(this.vidTimeout);
        if (this.recording) {
            console.log("recorder stop on pointer up");
            this.recorder.stop();
            this.toggleWatermark();
        } else {
            revealDownloadPage()
            sendTrackingEvent('User took photo');
            hideDraw();
            awaitTimer(500).then(() => {
                this.toggleWatermark();
                awaitTimer(100).then(() => {
                    this.imgData = this.renderer.instance.domElement.toDataURL("image/png", 0.8);
                    this.downloadImage.src = this.imgData;
                    //this.hidePhotofeature();
                    //awaitTimer(100).then(() => {
                    this.toggleWatermark();
                    revealDownloadData(this.isVid);
                    triggerDrawControl('pause')
                    //})
                })
            })
        }
    };

    private async save() {
        let data = this.isVid ? this.vidData : this.imgData;
        const image = await fetch(data);
        const blob = await image.blob();
        this.saveToDownloads(blob);
    };

    private share() {
        let data = this.isVid ? this.vidData : this.imgData;
        this.navigatorShare(data);
    };

    public triggerDrawControl(trigger: string) {
        console.log('triggerDrawControl', trigger)
        switch (trigger) {
            case 'cameraDown':
                this.cameraDown()
                break;
            case 'cameraUp':
                this.cameraUp();
                break;
            case 'download_back':
                this.resetVideo();
                hideDownloadPage()
                revealDraw();
                break
            case 'download_save':
                this.save();
                break
            case 'download_share':
                this.share();
                break
        }
    }


}
